import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { TrendingUp, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const SLUG_TO_PATH = {
    'dashboard': '/dashboard',
    'carlo-agent': '/carlo-agent',
    'sales': '/sales',
    'inventory': '/inventory',
    'employees': '/employees',
    'events': '/events',
    'tasks': '/tasks',
    'orders': '/orders',
    'setup': '/setup',
    'finance-performance': '/finance-performance',
    'products': '/products',
  };

  const getRedirectPath = (userData) => {
    if (!userData || userData.role !== 'staff') return '/dashboard';
    const pages = userData.allowed_pages;
    if (!pages) return '/dashboard';
    const firstSlug = Array.isArray(pages) ? pages[0] : Object.keys(pages)[0];
    return (firstSlug && SLUG_TO_PATH[firstSlug]) || '/dashboard';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await login(username, password);
      navigate(getRedirectPath(userData), { replace: true });
    } catch (err) {
      setError(t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#f8f7f4]">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden bg-[#1a4731]">
        {/* Decorative elements */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-1/2 right-16 w-44 h-44 rounded-full bg-white/[0.03]" />

        <div className="flex items-center gap-3 relative z-10">
          <div className="w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-bold text-xl tracking-tight">Carlo</span>
        </div>

        <div className="relative z-10">
          <h1 className="text-5xl font-bold text-white leading-[1.1] mb-5 tracking-tight">
            Smart<br />Restaurant<br />Operations
          </h1>
          <p className="text-white/50 text-lg leading-relaxed max-w-xs">
            Manage your sales, inventory, staff and analytics — all in one place.
          </p>
        </div>

        <p className="text-white/25 text-sm relative z-10">© {new Date().getFullYear()} Carlo</p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-[380px]">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-10 lg:hidden">
            <div className="w-11 h-11 bg-[#1a4731] rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 tracking-tight">Carlo</span>
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mb-1.5">{t('login.title')}</h2>
          <p className="text-slate-400 text-sm mb-8">{t('login.subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">
                {t('login.username')}
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a4731]/25 focus:border-[#1a4731]/40 transition-all"
                required
                autoFocus
                autoComplete="username"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-slate-600">
                  {t('login.password')}
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-slate-400 hover:text-[#1a4731] transition-colors"
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3.5 py-2.5 pr-10 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a4731]/25 focus:border-[#1a4731]/40 transition-all"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-sm px-3.5 py-2.5 rounded-lg border border-red-100">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-[#1a4731] hover:bg-[#153a27] text-white font-semibold rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-400">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="text-[#1a4731] hover:text-[#153a27] font-medium transition-colors">
              {t('login.registerLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
